import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaStar, FaShoppingCart, FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';

const ProductCard = ({ product, viewMode = 'grid' }) => {
    const [isHovered, setIsHovered] = useState(false);

    const addToCart = (e) => {
        e.preventDefault();
        const cart = JSON.parse(localStorage.getItem('tfm_cart') || '[]');
        const existingItem = cart.find(item => item.id === product.id);
        
        if (existingItem) {
            existingItem.quantity += 1;
        } else {
            cart.push({ ...product, quantity: 1 });
        }
        
        localStorage.setItem('tfm_cart', JSON.stringify(cart));
        window.dispatchEvent(new Event('cartUpdated'));
        toast.success(`${product.name} added to cart!`);
    };

    if (viewMode === 'list') {
        return (
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className="flex flex-col md:flex-row">
                    <div className="md:w-48 h-48 relative overflow-hidden">
                        {product.image_url ? (
                            <img 
                                src={product.image_url} 
                                alt={product.name}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                                <span className="text-4xl">🧸</span>
                            </div>
                        )}
                    </div>
                    <div className="flex-1 p-6">
                        <Link to={`/products/${product.id}`}>
                            <h3 className="text-xl font-semibold text-gray-800 mb-2 hover:text-purple-600 transition">
                                {product.name}
                            </h3>
                        </Link>
                        <div className="flex items-center gap-2 mb-3">
                            <div className="flex text-yellow-400">
                                <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                            </div>
                            <span className="text-sm text-gray-500">(12 reviews)</span>
                        </div>
                        <p className="text-gray-600 mb-4 line-clamp-2">{product.description}</p>
                        <div className="flex items-center justify-between">
                            <div>
                                <span className="text-2xl font-bold text-purple-600">£{product.price}</span>
                                {product.stock_quantity > 0 ? (
                                    <span className="ml-2 text-sm text-green-600">In Stock</span>
                                ) : (
                                    <span className="ml-2 text-sm text-red-600">Out of Stock</span>
                                )}
                            </div>
                            <button 
                                onClick={addToCart}
                                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition flex items-center gap-2"
                            >
                                <FaShoppingCart /> Add to Cart
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div 
            className="group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Link to={`/products/${product.id}`}>
                <div className="relative h-64 overflow-hidden">
                    {product.image_url ? (
                        <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                        />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                            <span className="text-6xl">🧸</span>
                        </div>
                    )}
                    
                    {/* Overlay on hover */}
                    <div className={`absolute inset-0 bg-black/50 flex items-center justify-center gap-4 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                        <button 
                            onClick={(e) => {
                                e.preventDefault();
                                window.location.href = `/products/${product.id}`;
                            }}
                            className="bg-white text-purple-600 p-3 rounded-full hover:bg-purple-600 hover:text-white transition transform hover:scale-110"
                        >
                            <FaEye />
                        </button>
                        <button 
                            onClick={(e) => {
                                e.preventDefault();
                                addToCart(e);
                            }}
                            className="bg-white text-purple-600 p-3 rounded-full hover:bg-purple-600 hover:text-white transition transform hover:scale-110"
                        >
                            <FaShoppingCart />
                        </button>
                    </div>
                    
                    {/* Badge */}
                    {product.featured && (
                        <div className="absolute top-4 left-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                            Featured
                        </div>
                    )}
                </div>
                
                <div className="p-5">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-purple-600 font-semibold">{product.category}</span>
                        <div className="flex items-center gap-1 text-yellow-400">
                            <FaStar className="text-xs" />
                            <FaStar className="text-xs" />
                            <FaStar className="text-xs" />
                            <FaStar className="text-xs" />
                            <FaStar className="text-xs" />
                        </div>
                    </div>
                    
                    <h3 className="font-semibold text-lg text-gray-800 mb-2 line-clamp-1 group-hover:text-purple-600 transition">
                        {product.name}
                    </h3>
                    
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {product.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold text-purple-600">
                            £{product.price}
                        </span>
                        {product.stock_quantity > 0 ? (
                            <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">In Stock</span>
                        ) : (
                            <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">Out of Stock</span>
                        )}
                    </div>
                </div>
            </Link>
        </div>
    );
};

export default ProductCard;